import Swiper from 'swiper/js/swiper.min';

const slideLinkSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
    '.link-download'
].join(', ');

function getActiveSlide(swiper) {
    return swiper.el.querySelector('.swiper-slide.swiper-slide-active') ?? swiper.slides[swiper.activeIndex] ?? null;
}

function getActiveSlideLinks(swiper) {
    const activeSlide = getActiveSlide(swiper);

    if (!activeSlide) {
        return [];
    }

    return Array.from(activeSlide.querySelectorAll(slideLinkSelector)).filter((element) => {
        return element.getAttribute('tabindex') !== '-1';
    });
}

function getFocusableSlides(swiper) {
    const activeSlide = getActiveSlide(swiper);
    const allowVisibleSlides = swiper.el.classList.contains('content-slider');

    return Array.from(swiper.slides || []).filter((slide) => {
        return allowVisibleSlides
            ? slide.classList.contains('swiper-slide-visible')
            : (slide === activeSlide || slide.classList.contains('swiper-slide-active'));
    });
}

function getFocusableSlideLinks(swiper) {
    return getFocusableSlides(swiper).reduce((links, slide) => {
        const slideLinks = Array.from(slide.querySelectorAll(slideLinkSelector)).filter((element) => {
            return element.getAttribute('tabindex') !== '-1';
        });

        return links.concat(slideLinks);
    }, []);
}

function hasFocusableSliderControls(swiper) {
    return swiper.el.querySelector(
        '.swiper-controls a[role="button"], .swiper-controls button, .swiper-button-prev, .swiper-button-next, .swiper-button-pause-play'
    ) !== null;
}

function getSliderControl(swiper, selector) {
    return swiper.el.querySelector(`.swiper-controls ${selector}`) ?? swiper.el.querySelector(selector);
}

function focusActiveSlideLink(swiper, position = 'first') {
    const activeLinks = getActiveSlideLinks(swiper);

    if (activeLinks.length === 0) {
        return;
    }

    const targetLink = position === 'last'
        ? activeLinks[activeLinks.length - 1]
        : activeLinks[0];

    targetLink.focus();
}

function focusFirstFocusableElement(swiper) {
    const focusableLinks = getFocusableSlideLinks(swiper);

    if (focusableLinks.length > 0) {
        focusableLinks[0].focus();
        return;
    }

    const prevControl = getSliderControl(swiper, '.swiper-button-prev');
    const nextControl = getSliderControl(swiper, '.swiper-button-next');
    const pausePlayControl = getSliderControl(swiper, '.swiper-button-pause-play');
    const fallbackControl = prevControl ?? pausePlayControl ?? nextControl;

    if (fallbackControl) {
        fallbackControl.focus();
    }
}

function normalizeSwiperContainerFocus(swiper) {
    swiper.el.removeAttribute('tabindex');

    if (swiper.wrapperEl) {
        swiper.wrapperEl.removeAttribute('tabindex');
    }
}

function bindContainerFocusRedirect(swiper) {
    if (swiper.el.hasAttribute('data-swiper-container-focus-bound')) {
        return;
    }

    swiper.el.setAttribute('data-swiper-container-focus-bound', 'true');
    swiper.el.addEventListener('focusin', (event) => {
        if (event.target !== swiper.el) {
            return;
        }

        window.requestAnimationFrame(() => {
            focusFirstFocusableElement(swiper);
        });
    });
}

function findFocusableOutsideSlider(swiperEl, currentEl, direction) {
    const focusables = Array.from(document.querySelectorAll(
        'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
    ));
    const startIndex = focusables.indexOf(currentEl);
    if (startIndex === -1) {
        return null;
    }

    const step = direction === 'prev' ? -1 : 1;
    for (let i = startIndex + step; i >= 0 && i < focusables.length; i += step) {
        const candidate = focusables[i];
        if (swiperEl.contains(candidate)) {
            continue;
        }
        if (candidate.closest('[inert]')) {
            continue;
        }
        if (candidate.offsetParent === null && candidate !== document.body) {
            continue;
        }
        return candidate;
    }
    return null;
}

function bindSlideLinkNavigation(swiper) {
    if (swiper.el.hasAttribute('data-swiper-link-navigation-bound')) {
        return;
    }

    swiper.el.setAttribute('data-swiper-link-navigation-bound', 'true');
    swiper.el.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !event.shiftKey) {
            return;
        }

        const prevControl = getSliderControl(swiper, '.swiper-button-prev');
        if (event.target !== prevControl) {
            return;
        }

        // prev-Button is positioned in DOM after the slides — browser default Shift+Tab
        // would navigate back into the slider's last link (loop). Manually move focus
        // to the previous focusable element outside the slider.
        const target = findFocusableOutsideSlider(swiper.el, event.target, 'prev');
        if (target) {
            event.preventDefault();
            target.focus();
        }
    });
}

function updateSlideFocusability(swiper) {
    const focusableSlides = new Set(getFocusableSlides(swiper));

    Array.from(swiper.slides || []).forEach((slide) => {
        const isFocusableSlide = focusableSlides.has(slide);

        slide.inert = !isFocusableSlide;
        slide.setAttribute('aria-hidden', isFocusableSlide ? 'false' : 'true');

        slide.querySelectorAll(slideLinkSelector).forEach((element) => {
            if (!element.hasAttribute('data-swiper-original-tabindex')) {
                element.setAttribute(
                    'data-swiper-original-tabindex',
                    element.hasAttribute('tabindex') ? element.getAttribute('tabindex') : ''
                );
            }
            if (!element.hasAttribute('data-swiper-original-pointer-events')) {
                element.setAttribute(
                    'data-swiper-original-pointer-events',
                    element.style.pointerEvents
                );
            }

            if (isFocusableSlide) {
                const originalTabIndex = element.getAttribute('data-swiper-original-tabindex');
                const originalPointerEvents = element.getAttribute('data-swiper-original-pointer-events');

                if (originalTabIndex === '') {
                    if (element.classList.contains('link-download')) {
                        element.setAttribute('tabindex', '0');
                    } else {
                        element.removeAttribute('tabindex');
                    }
                } else {
                    element.setAttribute('tabindex', originalTabIndex);
                }

                element.style.pointerEvents = originalPointerEvents;
                return;
            }

            element.setAttribute('tabindex', '-1');
            element.style.pointerEvents = 'none';

            if (element === document.activeElement) {
                element.blur();
            }
        });
    });
}

function syncSlideFocusability(swiper) {
    window.requestAnimationFrame(() => {
        normalizeSwiperContainerFocus(swiper);
        bindContainerFocusRedirect(swiper);
        bindSlideLinkNavigation(swiper);
        updateSlideFocusability(swiper);
    });
}

export function createSlider($swiperContainer, swiperOptions) {
    // console.warn("createSlider");
    let $slides = document.querySelectorAll('.swiper-slide');
    if ($slides.length > 1 && !$swiperContainer[0].classList.contains('initialized')) {
        $swiperContainer.addClass('initialized').removeClass('carousel-off');

        let $swiperContainerId = $swiperContainer[0].id;
        let slidesPerView = 1;
        if ($swiperContainer.data('slides')) {
            slidesPerView = $swiperContainer.data('slides');
        }
        let swiperOptions = {
            direction: 'horizontal',
            slidesPerView: slidesPerView,
            watchSlidesVisibility: true,
            watchSlidesProgress: true,
            navigation: {
                nextEl: '#' + $swiperContainerId + ' .swiper-button-next',
                prevEl: '#' + $swiperContainerId + ' .swiper-button-prev'
            },
            a11y: {
                enabled: true,
                prevSlideMessage: 'Vorherigen Slide anzeigen',
                nextSlideMessage: 'Nächsten Slide anzeigen',
                paginationBulletMessage: 'Gehe zum Slide {{index}}'
            },
            on: {
                init() {
                    syncSlideFocusability(this);
                },
                slideChange() {
                    syncSlideFocusability(this);
                },
                slideChangeTransitionEnd() {
                    syncSlideFocusability(this);
                }
            },
            loop: true,
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            }
        };

        let autoplayInterval = $swiperContainer.data('interval');
        if (autoplayInterval && autoplayInterval > 1000) {
            swiperOptions.autoplay = {
                delay: autoplayInterval,
                disableOnInteraction: false
            };
        }
        return new Swiper($swiperContainer, swiperOptions);
    }
}

export function createSliderHeader($swiperContainer, swiperOptions) {
    //console.warn("createSliderHeader");
    let $slides = document.querySelectorAll('.swiper-slide');
    if ($slides.length > 1 && !$swiperContainer[0].classList.contains('initialized')) {
        $swiperContainer.addClass('initialized').removeClass('carousel-off');

        let $swiperContainerId = $swiperContainer[0].id;
        let slidesPerView = 1;
        if ($swiperContainer.data('slides')) {
            slidesPerView = $swiperContainer.data('slides');
        }
        let swiperOptions = {
            direction: 'horizontal',
            slidesPerView: slidesPerView,
            watchSlidesVisibility: true,
            watchSlidesProgress: true,
            navigation: {
                nextEl: '#' + $swiperContainerId + ' .swiper-button-next',
                prevEl: '#' + $swiperContainerId + ' .swiper-button-prev'
            },
            a11y: {
                enabled: true,
                prevSlideMessage: 'Vorherigen Slide anzeigen',
                nextSlideMessage: 'Nächsten Slide anzeigen',
                paginationBulletMessage: 'Gehe zum Slide {{index}}'
            },
            on: {
                init() {
                    syncSlideFocusability(this);
                },
                slideChange() {
                    syncSlideFocusability(this);
                },
                slideChangeTransitionEnd() {
                    syncSlideFocusability(this);
                }
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
                bulletElement: 'button',
                role: ''
            },
            loop: true,
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            }
        };

        let autoplayInterval = $swiperContainer.data('interval');
        if (autoplayInterval && autoplayInterval > 1000) {
            swiperOptions.autoplay = {
                delay: autoplayInterval,
                disableOnInteraction: false
            };
        }
        return new Swiper($swiperContainer, swiperOptions);
    }
}
